﻿import TKSelectRedirect from '@tk/controls/tk.select.redirect';
import render from '@tk/utilities/tk.render';
import KWArtSpecTable from '@/components/kw.art.spec.table';
import KWAsync from '@/components/kw.async';

interface Attribute {
    attr: string;
    label: string;
    value?: string;
}

interface ArticleVariant {
    id: string;
    attributes: Attribute[];
}
export default class KWSelectRedirect extends TKSelectRedirect {
    artSpecTable?: KWArtSpecTable;
    asynContainer?: NodeListOf<KWAsync>;
    constructor() {
        super();

        this.artSpecTable = document.querySelector('[data-tk-artspectable]') || undefined;
        this.asynContainer = document.querySelectorAll('kw-async');
    }

    async handleChangeSelection(event: InputEvent) {
        const target = event.target as HTMLSelectElement;
        const id = this.getIdFromMultipleSelection(target);
        if (!id) return;
        await this.getAsyncContentById(id);
        this.artSpecTable && this.artSpecTable.fetch(id);

        if (this.asynContainer && this.asynContainer.length > 0) {
            this.reloadContent(id);
        }
    }

    reloadContent(id:string):void {
        this.asynContainer!.forEach((asyncElement) => {
            asyncElement.getContentByArticle(id);
        });
    }

    getIdFromMultipleSelection(currentFilter: HTMLSelectElement): string | undefined {
        const currentFilterId = this.selections.findIndex(
            (selection) => selection.fieldElement === currentFilter,
        );

        const attributes = this.selections.map((selection) => ({
            attr: selection.attribute,
            value: selection.fieldElement.value,
        })) as Attribute[];

        let matchResults = this.compareAttributes(attributes, this.variants)
            .filter((variant) => variant.matches[currentFilterId] === true)
            .sort((a, b) => b.matchCount - a.matchCount)
            .filter((variant, _, arr) => variant.matchCount === arr[0].matchCount);

        if (matchResults.length === 0) {
            throw new Error('kw.select.redirect: no matches');
        }

        for (let index = 0; matchResults.length > 1 && index < this.selections.length; index++) {
            if (index === currentFilterId) break;
            matchResults = matchResults.filter((variant) => variant.matches[index]);
        }

        return matchResults[0]?.id;
    }

    // eslint-disable-next-line class-methods-use-this
    compareAttributes(firstArray: Attribute[], secondArray: ArticleVariant[]) {
        return secondArray.map((item) => {
            const matches = firstArray.map((ref) => {
                const match = item.attributes.find((attr) => attr.attr === ref.attr);
                return match ? match.value === ref.value : false;
            });

            return {
                id: item.id,
                matches,
                matchCount: matches.filter(Boolean).length,
            };
        });
    }

    /**
     * Creates a select box with the given configuration and attaches change event listener.
     *
     * @param {Object} params - The parameters for creating the select box.
     * @param {string} params.id - The ID for the select box element.
     * @param {string} params.label - The label for the select box element.
     * @param {string[]} params.values - The values to populate the select box options.
     * @param {string} [params.selectedValue] - The value to be selected by default, if any.
     * @returns {{ wrapper: HTMLElement, fieldElement: HTMLSelectElement } | undefined}
     * An object containing the wrapper and field element, or undefined if the field element is not found.
     */
    createSelectbox({
        id,
        label,
        values,
        selectedValue,
    }: {
        id: string;
        label: string;
        values: string[];
        selectedValue?: string;
    }): {
        wrapper: HTMLElement;
        fieldElement: HTMLSelectElement;
    } | undefined {
        const wrapper = render(`
            <div class="tk-form-select">
                <label for="form-variants-${id}">${label}</label>
                <div class="tk-form__wrapper">
                    <select
                        id="form-variants-${id}"
                        name="form-variants-${id}"
                        data-tk-product-variants
                    >
                        ${values.map((value) => {
        const escapedValue = value.replace(/"/g, '&quot;');
        return `<option value="${escapedValue}" ${selectedValue === value && 'selected'}>${value}</option>`;
    }).join('\n')}
                    </select>
                </div>
            </div>
        `);
        const fieldElement = wrapper.querySelector('select');
        if (!fieldElement) return;
        const onChange = this.handleChangeSelection.bind(this);
        this.pushListener({ event: 'input', element: fieldElement, action: onChange });
        return {
            wrapper,
            fieldElement,
        };
    }
}
